import { useEffect, useId, useRef, useState } from "react";
import { useAuth } from "../../lib/auth-context.js";

/** Field-level problems, keyed by field so each message can sit next to its input.
 *
 * `noValidate` is on the form deliberately - the browser's own bubbles are inconsistent across
 * browsers, vanish on the next click, and cannot be styled to match a console that a guard
 * reads at 2am. But suppressing them and replacing them with NOTHING is how pressing "Sign in"
 * with both fields empty came to produce no message at all: the button appeared to do nothing.
 * docs/KNOWN-PROBLEMS.md finding 19, docs/FRONTEND-AUDIT.md F-P2-2. */
interface FieldErrors {
  email?: string | undefined;
  password?: string | undefined;
}

function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (email.trim() === "") errors.email = "Enter your email address.";
  else if (!email.includes("@")) errors.email = "That does not look like an email address.";
  if (password === "") errors.password = "Enter your password.";
  return errors;
}

export function LoginPage() {
  const { signIn, endedReason } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  /** Nothing is marked wrong until the field has been left or the form submitted - telling
   * someone their email is invalid after one keystroke is noise, not help. */
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });
  const [busy, setBusy] = useState(false);

  const emailId = useId();
  const passwordId = useId();
  const emailErrorId = useId();
  const passwordErrorId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  function revalidate(nextEmail: string, nextPassword: string) {
    // Only re-checks fields already touched, so a message appears once and then updates as the
    // person fixes it, rather than appearing while they are still typing the first one.
    const all = validate(nextEmail, nextPassword);
    setFieldErrors({
      ...(touched.email && all.email ? { email: all.email } : {}),
      ...(touched.password && all.password ? { password: all.password } : {}),
    });
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const problems = validate(email, password);
    if (problems.email || problems.password) {
      setTouched({ email: true, password: true });
      setFieldErrors(problems);
      // Focus goes to the first bad field, so a keyboard or screen-reader user is taken to the
      // thing to fix rather than left on the button.
      if (problems.email) emailRef.current?.focus();
      else passwordRef.current?.focus();
      return;
    }

    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      // Never a raw error object - a sentence a person can act on.
      setError(err instanceof Error ? err.message : "Sign-in failed. Please try again.");
      setPassword("");
      setShowPassword(false);
      passwordRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="signin">
      <div className="signin-card">
        <h1>Door Control</h1>
        <p className="signin-sub">Marktine · Mantra mBio7S</p>

        {endedReason ? (
          <div className="form-error" role="status">
            {endedReason}
          </div>
        ) : null}

        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}

        <form onSubmit={(event) => void onSubmit(event)} noValidate>
          <div className="field">
            <label htmlFor={emailId}>Email</label>
            <input
              id={emailId}
              ref={emailRef}
              type="email"
              autoComplete="username"
              value={email}
              aria-invalid={fieldErrors.email !== undefined}
              aria-describedby={fieldErrors.email ? emailErrorId : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
                revalidate(event.target.value, password);
              }}
              onBlur={() => {
                setTouched((t) => ({ ...t, email: true }));
                const all = validate(email, password);
                setFieldErrors((f) => ({ ...f, email: all.email }));
              }}
            />
            {fieldErrors.email ? (
              <p className="field-error" id={emailErrorId} role="alert">
                {fieldErrors.email}
              </p>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor={passwordId}>Password</label>
            <div className="input-with-button">
              <input
                id={passwordId}
                ref={passwordRef}
                // A mistyped, unseen password is the commonest sign-in support request there is.
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                aria-invalid={fieldErrors.password !== undefined}
                aria-describedby={fieldErrors.password ? passwordErrorId : undefined}
                onChange={(event) => {
                  setPassword(event.target.value);
                  revalidate(email, event.target.value);
                }}
                onBlur={() => {
                  setTouched((t) => ({ ...t, password: true }));
                  const all = validate(email, password);
                  setFieldErrors((f) => ({ ...f, password: all.password }));
                }}
              />
              <button
                type="button"
                className="btn"
                aria-pressed={showPassword}
                aria-label={showPassword ? "Hide the password" : "Show the password"}
                onClick={() => setShowPassword((visible) => !visible)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {fieldErrors.password ? (
              <p className="field-error" id={passwordErrorId} role="alert">
                {fieldErrors.password}
              </p>
            ) : null}
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="signin-foot">
          Accounts carry a role. A <code>door_operator</code> can pulse a door open; holding a door
          locked or unlocked is a persistent override and needs <code>security_admin</code>.
        </p>
      </div>
    </main>
  );
}
